import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, ArrowLeft, ArrowRight, ImageIcon } from "lucide-react";
import api from "@/lib/api";

const resolveSrc = (u) => u || "";

export default function ProductImagesEditor({ values, onChange }) {
  const mainRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const gallery = (Array.isArray(values.gallery) ? values.gallery : [])
    .map((g) => (typeof g === "string" ? { image: g, label: "" } : g));

  const upload = async (file, cb) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data.url);
      toast.success("Image uploaded — click Save below to keep the change");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= gallery.length) return;
    const next = [...gallery];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ gallery: next });
  };

  const removeAt = (i) => onChange({ gallery: gallery.filter((_, idx) => idx !== i) });

  const setLabel = (i, label) => {
    const next = [...gallery];
    next[i] = { ...next[i], label };
    onChange({ gallery: next });
  };

  return (
    <div className="border border-line bg-mist/50 p-5" data-testid="product-images-editor">
      {/* Main image */}
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Main Product Image</p>
      <div className="mt-3 flex items-start gap-4">
        <div className="flex h-28 w-36 shrink-0 items-center justify-center border border-line bg-white">
          {values.image ? (
            <img src={resolveSrc(values.image)} alt="Main product" data-testid="product-main-image-preview"
              className="h-full w-full object-contain" />
          ) : (
            <ImageIcon size={22} className="text-slate-300" />
          )}
        </div>
        <div className="flex-1 space-y-2.5">
          <button type="button" onClick={() => mainRef.current?.click()} disabled={busy} data-testid="product-image-upload-button"
            className="inline-flex items-center gap-2 border border-accent px-4 py-2 text-[13px] font-semibold text-accent transition-colors hover:bg-accent hover:text-white disabled:opacity-60">
            <UploadCloud size={14} /> {values.image ? "Replace Image" : "Upload Image"}
          </button>
          <input ref={mainRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="sr-only" data-testid="product-image-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, (url) => onChange({ image: url })); }} />
          {values.image && (
            <button type="button" onClick={() => onChange({ image: "" })} data-testid="product-image-remove-button"
              className="ml-2 border border-line px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-steel hover:border-red-400 hover:text-red-600">
              Remove
            </button>
          )}
          <div>
            <label htmlFor="ce-image-alt" className="label-field">Image Alt Text (for SEO)</label>
            <input id="ce-image-alt" data-testid="product-image-alt-input" className="input-field"
              placeholder="e.g. STJ Dynamic Pass Box in SS 304"
              value={values.image_alt || ""} onChange={(e) => onChange({ image_alt: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Gallery */}
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Image Gallery ({gallery.length})</p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {gallery.map((g, i) => (
          <div key={`${g.image}-${i}`} className="border border-line bg-white" data-testid={`gallery-item-${i}`}>
            <div className="flex h-20 items-center justify-center p-1.5">
              <img src={resolveSrc(g.image)} alt={g.label || `Gallery ${i + 1}`} className="max-h-full max-w-full object-contain" loading="lazy" />
            </div>
            <input value={g.label || ""} onChange={(e) => setLabel(i, e.target.value)}
              placeholder="Caption / alt text" data-testid={`gallery-label-${i}`}
              className="w-full border-t border-line bg-white px-2 py-1 text-[11px] text-ink outline-none placeholder:text-slate-300" />
            <div className="flex items-center justify-between border-t border-line px-1.5 py-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move earlier"
                data-testid={`gallery-move-left-${i}`}
                className="flex h-6 w-6 items-center justify-center text-steel hover:text-accent disabled:opacity-30">
                <ArrowLeft size={12} />
              </button>
              <span className="font-mono text-[9px] text-slate-400">{i + 1}</span>
              <button type="button" onClick={() => move(i, 1)} disabled={i === gallery.length - 1} aria-label="Move later"
                data-testid={`gallery-move-right-${i}`}
                className="flex h-6 w-6 items-center justify-center text-steel hover:text-accent disabled:opacity-30">
                <ArrowRight size={12} />
              </button>
              <button type="button" onClick={() => removeAt(i)} aria-label="Remove image"
                data-testid={`gallery-remove-${i}`}
                className="flex h-6 w-6 items-center justify-center text-steel hover:text-red-600">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => galRef.current?.click()} disabled={busy} data-testid="gallery-add-image-button"
          className="flex h-20 flex-col items-center justify-center gap-1.5 border border-dashed border-line bg-white text-[12px] font-medium text-steel transition-colors hover:border-accent/50 hover:text-accent disabled:opacity-60">
          <UploadCloud size={16} /> Add Image
        </button>
        <input ref={galRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="sr-only" data-testid="gallery-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, (url) => onChange({ gallery: [...gallery, { image: url, label: "" }] })); }} />
      </div>
      <p className="mt-3 text-xs text-slate-400">
        Upload JPG, PNG or WebP from your computer (max 10 MB). Use the arrows to reorder. Changes go live after you press Save.
      </p>
    </div>
  );
}
